import Vue, { CreateElement } from 'src/vue'
import { Component } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'
import ApiClient from '@src/lib/api_client'
import { Loader } from '@src/components/loading-screen/loader/loader'
import BlogPost from '@src/data/blog-post'
import { Body1, Subtitle2 } from '@src/elements/typography/typography'
import { Spacer } from '@src/elements/spacer/spacer'
import { Post } from '@src/routes/blog/post'
import { TimeSwitcher } from '@src/elements/time-switch/time-switch'
import { Input } from '@src/elements/input/input'
import { Link } from '@src/elements/link/link'
import { API } from '@src/lib/api_path'
import { ActionBar } from '@src/components/action-bar/action-bar'
import { track } from '@src/util/functions'
import { convertYYYYMMDDToDate } from '@src/util/date-format'
import { BlogPostFilters } from '@src/state/store'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'

import './blog.sass'

type BlogPostsResponse = { blog_posts: BlogPost[] }

@Component({
  computed: {
    filters () {
      return this.$store.state.blog.blogPostsFilters
    }
  }
})
export class Blog extends Vue {

  private apiClient: ApiClient = new ApiClient(API.TRAINERS_API.BLOG_POSTS)
  private loading: boolean = true

  private posts: BlogPost[] = []
  private filters: BlogPostFilters

  constructor () {
    super()
    track('/blog')
  }

  changeTimeFrame (timeFrame: string, periods: any[]) {
    if (periods.length > 0) {
      this.$store.dispatch('filterBlogPosts', {
        from: convertYYYYMMDDToDate(periods[0].from),
        to: convertYYYYMMDDToDate(periods[periods.length - 1].to),
        text: this.filters.text
      })
    }
  }

  filterByText (value: string) {
    this.$store.dispatch('filterBlogPosts', {
      text: value
    })
  }

  filter (): BlogPost[] {
    const filterByTime = (post: BlogPost) => {
      if (!this.filters.from || !this.filters.to) {
        return true
      }
      const publishedAt = convertYYYYMMDDToDate(post.publishedAt)
      return this.filters.from <= publishedAt && publishedAt <= this.filters.to
    }

    // check the title and content for each language; persist the first language found, to select it when displaying the post
    const filterByText = (post: BlogPost) => {
      if (!this.filters.text) {
        post.preselectedLanguage = undefined
        return true
      }
      let needle = new RegExp(this.filters.text.trim() || '', 'i')
      // posts are not translated to every language, the missing ones are null
      const matches = (...texts: string[]) => texts.some(text => !!text && !!text.match(needle))
      let preselectedLanguage
      if (matches(post.titleEn, post.contentEn)) {
        preselectedLanguage = 'En'
      } else if (matches(post.titleDe, post.contentDe)) {
        preselectedLanguage = 'De'
      } else if (matches(post.titleJp, post.contentJp)) {
        preselectedLanguage = 'Jp'
      }
      post.preselectedLanguage = preselectedLanguage

      return preselectedLanguage
    }

    return this.posts.filter((post: BlogPost) => filterByTime(post) && filterByText(post))
  }

  resetFilters () {
    this.$store.dispatch('filterBlogPosts', { from: undefined, to: undefined, text: undefined })
  }

  render (h: CreateElement) {
    if (this.loading) {
      return <Loader />
    }

    if (this.posts.length === 0) {
      return <div class='empty-list'>
        <div class='empty-list-message'>
          <Body1 align='center'>{this.$t('PAGE.BLOG.NO_POSTS')}</Body1>
        </div>
      </div>
    }

    let filteredPosts = this.filter()
      .sort((a, b) => a.publishedAt < b.publishedAt ? 1 : a.publishedAt > b.publishedAt ? -1 : 0)
      .map((post: BlogPost) => <Post post={post} />)

    return <div class='blog-wrapper'>
      <ActionBar>
        <Subtitle2 class='header'>{this.$t('PAGE.BLOG.LATEST_NEWS')}</Subtitle2>
        <TimeSwitcher dateFilters={[DateFilterType.WEEK, DateFilterType.MONTH, DateFilterType.YEAR]} change={this.changeTimeFrame} align='right' allowFutureDate={false}/>
        <Input ref='textFilter' search={true} class='search-posts' placeholder={this.$t('PAGE.BLOG.SEARCH_PLACEHOLDER')} valueChanged={this.filterByText}
               value={this.filters.text} />
      </ActionBar>

      {filteredPosts.length === 0 ?
        <div class='empty-list no-match'>
          <div class='empty-list-message'>
            <Spacer />
            {this.filters.text ?
              <Body1 align='center'>{this.$t('PAGE.BLOG.NOT_FOUND.FILTER.NAME', { query: this.filters.text })}</Body1>
              : <Body1 align='center'>{this.$t('PAGE.BLOG.NOT_FOUND.FILTER.GENERIC')}</Body1>}
            <Spacer />
            <Link variant='colored' align='center' click={this.resetFilters} label={this.$t('PAGE.BLOG.RESET_FILTERS')} ref='resetBtn' />
          </div>
        </div>

        : <div class='column-wrapper scroll-content'>{filteredPosts}</div>}
    </div>
  }

  created () {
    return this.loadPosts()
  }

  updated () {
    document.title = `${this.$root.$t('PIXFORMANCE_PLATFORM')} - ${this.$root.$t('PAGE.BLOG.MENU_TITLE')}`
  }

  loadPosts () {
    return this.apiClient.get<BlogPostsResponse>()
      .then((response: BlogPostsResponse) => this.posts = response.blog_posts.map(post => new BlogPost(camelcaseKeys(post, { deep: true }))))
      .catch(error => console.log('cannot load posts', error))
      .finally(() => this.loading = false)
  }
}
